import { type MapIssue } from './validate.js';
import { type TiledMap } from './types.js';

/**
 * Limits for tile animations (ADR-0028). The shortest frame matches the fastest rate in the style
 * guide (12 fps walk, 83 ms in Aseprite), so ambient tiles never flicker faster than a character.
 */
export const TILE_ANIMATION_LIMITS = {
  minFrameMs: 80,
  maxFrames: 32,
} as const;

export interface TileAnimationFrame {
  /** Global tile id shown during this frame. */
  readonly gid: number;
  readonly duration: number;
}

/** An animated tile: wherever `gid` is placed, the game cycles through `frames`. */
export interface TileAnimation {
  readonly gid: number;
  readonly frames: readonly TileAnimationFrame[];
  /** Sum of all frame durations in ms. */
  readonly cycleMs: number;
}

/**
 * Every animated tile of a map by its global id. Tiled stores animations per tileset with local
 * ids; this turns them into gids, so a renderer can look a placed tile up directly.
 */
export function tileAnimations(map: TiledMap): Map<number, TileAnimation> {
  const animations = new Map<number, TileAnimation>();
  for (const set of map.tilesets) {
    for (const tile of set.tiles ?? []) {
      const frames = (tile.animation ?? []).map((frame) => ({
        gid: set.firstgid + frame.tileid,
        duration: frame.duration,
      }));
      if (frames.length < 2) continue; // one frame is a still tile
      const cycleMs = frames.reduce((sum, frame) => sum + frame.duration, 0);
      animations.set(set.firstgid + tile.id, { gid: set.firstgid + tile.id, frames, cycleMs });
    }
  }
  return animations;
}

/**
 * The gid to show at `timeMs`. Every copy of a tile uses the same clock, so a row of animated
 * tiles stays in step, as in Tiled.
 */
export function animationFrameAt(animation: TileAnimation, timeMs: number): number {
  let t = ((timeMs % animation.cycleMs) + animation.cycleMs) % animation.cycleMs;
  for (const frame of animation.frames) {
    if (t < frame.duration) return frame.gid;
    t -= frame.duration;
  }
  return animation.frames[0]?.gid ?? animation.gid;
}

/** Checks that each animation stays inside its tileset and within the limits above. */
export function checkTileAnimations(map: TiledMap): MapIssue[] {
  const issues: MapIssue[] = [];
  const add = (code: string, message: string) => issues.push({ code, message });
  for (const set of map.tilesets) {
    for (const tile of set.tiles ?? []) {
      const where = `Tile ${tile.id} in tileset "${set.name}"`;
      if (tile.id >= set.tilecount) {
        add('tile-animation-unknown-tile', `${where} is outside the tileset`);
        continue;
      }
      const frames = tile.animation ?? [];
      if (frames.length > TILE_ANIMATION_LIMITS.maxFrames) {
        add(
          'tile-animation-too-long',
          `${where} has ${frames.length} frames; the maximum is ${TILE_ANIMATION_LIMITS.maxFrames}`,
        );
      }
      if (frames.some((frame) => frame.tileid >= set.tilecount)) {
        add('tile-animation-unknown-tile', `${where} has a frame outside the tileset`);
      }
      if (frames.some((frame) => frame.duration < TILE_ANIMATION_LIMITS.minFrameMs)) {
        add(
          'tile-animation-too-fast',
          `${where} has a frame shorter than ${TILE_ANIMATION_LIMITS.minFrameMs} ms`,
        );
      }
    }
  }
  return issues;
}
